import { Select } from '@byecode/ui'
import type { RichTextContentProtocol } from '@lighthouse/core'
import {
    emptyTextField,
    type FlowNode,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    NodeFieldPreview,
    OUTPUT_SINGLE_DATA_NODES,
    useAtomData
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'
import { useGetWechatDeliveryList } from '@/shared/reusable'

import * as CM from '../../styles'
import { toPlainRichText } from './constants'

interface DeliveryCompanyFieldProps {
    allParentNodes: FlowNode[]
    value?: RichTextContentProtocol
    onChange: (value: RichTextContentProtocol) => void
    required?: boolean
    label: string
    placeholder?: string
}

export const DeliveryCompanyField: React.FC<DeliveryCompanyFieldProps> = ({
    allParentNodes,
    value,
    onChange,
    required,
    label,
    placeholder
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const userOption = useGetUserRecordOptions()
    const { data: deliveryList } = useGetWechatDeliveryList(appId)

    const textUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType)),
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, workflowList]
    )

    const options = useMemo(
        () =>
            (deliveryList || [])
                .filter(item => item.deliveryId)
                .map(item => ({
                    label: `${item.deliveryName || item.deliveryId}（${item.deliveryId}）`,
                    value: item.deliveryId
                })),
        [deliveryList]
    )

    return (
        <CM.FieldItem>
            <CM.FieldTitle required={required}>{label}</CM.FieldTitle>
            {options.length > 0 && (
                <Select
                    searchable
                    style={{ width: '100%' }}
                    disabled={disabledWithVersion}
                    placeholder="从运力列表选择"
                    options={options}
                    onChange={deliveryId => {
                        if (deliveryId) {
                            onChange(toPlainRichText(deliveryId))
                        }
                    }}
                />
            )}
            <NodeFieldPreview
                innerTypes={getInputInnerTypeMatch('TEXT')}
                disabled={disabledWithVersion}
                field={emptyTextField}
                value={value}
                onBlur={onChange}
                autoHeight
                placeholder={placeholder || '填写物流公司编码，或插入变量'}
                userOption={userOption}
                upstreamOptions={textUpstreamOptions}
                dataSourceList={dataSourceList}
            />
        </CM.FieldItem>
    )
}
